import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { SalesStatistics } from "interface/sales";
import { GrideDateFormatter } from "ui/Date";

interface StoreStatisticsMobileListProps {
	items: SalesStatistics[];
	loading: boolean;
}

const formatWeight = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return `${asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
	})} kg`;
};

const formatNumber = (value?: number | null, fractionDigits = 0) => {
	const asNumber = Number(value || 0);
	return asNumber.toLocaleString("no-NO", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});
};

const formatVessel = (item: SalesStatistics) => {
	const registration = item.vesselRegistrationNumber?.trim();
	const vesselName = item.vesselDescription?.trim();

	if (registration && vesselName) {
		return `${registration} - ${vesselName}`;
	}

	return registration || vesselName || "-";
};

const StoreStatisticsMobileList: FC<StoreStatisticsMobileListProps> = ({
	items,
	loading,
}) => {
	const { t } = useTranslation();
	const empty = !loading && items.length === 0;

	return (
		<div className="store-statistics-list-mobile">
			{empty && (
				<div className="store-statistics-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const itemKey = `${item.landingNoteNumber || "nolanding"}-${item.lineNumber || itemIndex}-${itemIndex}`;

				return (
					<div key={itemKey} className="store-statistics-list-card">
						<div className="store-statistics-list-card-header">
							<div className="store-statistics-list-card-vessel">
								{item.productDescription || "-"}
							</div>
						</div>

						<div className="store-statistics-list-card-body">
							<div className="store-statistics-list-card-body-full">
								<span>{t("Vessel")}</span>
								<strong>{formatVessel(item)}</strong>
							</div>
							<div className="store-statistics-list-card-body-full">
								<span>{t("Terminal")}</span>
								<strong>{item.terminalDescription || "-"}</strong>
							</div>
							<div>
								<span>{t("Species")}</span>
								<strong>{item.specieDescription || "-"}</strong>
							</div>
							<div>
								<span>{t("FishingGear")}</span>
								<strong>{item.fishingGearDescription || "-"}</strong>
							</div>
							<div className="store-statistics-list-card-body-full">
								<span>{t("CatchArea")}</span>
								<strong>
									{item.catchAreaDescription || item.catchAreaCode || "-"}
									{item.catchAreaZone ? ` / ${item.catchAreaZone}` : ""}
								</strong>
							</div>
							<div>
								<span>{t("PreservationType")}</span>
								<strong>{item.productPreservationType || "-"}</strong>
							</div>
							<div>
								<span>{t("DeliveryDate")}</span>
								<strong>
									{item.deliveryDateTime ? (
										<GrideDateFormatter
											date={item.deliveryDateTime}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									) : (
										"-"
									)}
								</strong>
							</div>
						</div>

						<div className="store-statistics-list-card-footer">
							<div>
								<span>{t("Weight")}</span>
								<strong>{formatWeight(item.grossWeight)}</strong>
							</div>
							<div>
								<span>{t("RoundWeight")}</span>
								<strong>{formatWeight(item.roundWeight)}</strong>
							</div>
							<div>
								<span>{t("Quantity")}</span>
								<strong>{formatNumber(item.quantity)}</strong>
							</div>
							<div>
								<span>{t("NumberOfUnits")}</span>
								<strong>{formatNumber(item.numberOfUnits)}</strong>
							</div>
						</div>
					</div>
				);
			})}

			{loading && (
				<div className="store-statistics-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default StoreStatisticsMobileList;